import Vue from 'vue'
import router from '@/router'
import to from 'await-to-js'
import buildClient from '@/api/client'
import productGroupQuery from '@/api/product-group.gql'
import productReviewsQuery from '@/api/product-reviews.gql'
import { loadForAtLeast } from '@/helpers/submit'
import routeNames from '@/router/route-names'

const FIRST_REVIEW_PAGE = 1
export const REVIEWS_PER_PAGE = 3
export const BEST_REVIEW_RATING = 5
export const WORST_REVIEW_RATING = 1
export const INITIAL_SLIDE_INDEX = 0
export const TOPGALLERY_IMAGE_OPTIONS = {
  format: 'jpg',
  backgroundColor: 'f7f7f7',
}

export const PRODUCT_TYPES = {
  shoes: 'shoes',
  apparel: 'apparel',
  accessories: 'accessories',
}

export const GENDERS = {
  mens: 'mens',
  womens: 'womens',
  unisex: 'unisex',
}

GENDERS.default = GENDERS.mens

function redirectToFilterPage(hostname, locale) {
  window.location.href = `${hostname}/${locale}/explore`
}

function findVariantIndex(variants, routeColors) {
  return variants.findIndex(variant => {
    return routeColors.every(routeColor => {
      return variant.color.toLowerCase().indexOf(routeColor) > -1
    })
  })
}

function createRouteColors(colorString) {
  return colorString.toLowerCase().replace(/-?[^0-9a-zA-Z-]+-?/g, '-')
}

export default {
  namespaced: true,
  state: {
    currentGalleryItemIndex: 0,
    currentVariantIndex: 0,
    hasFetchedFirstReviews: false,
    isAllItemsExpanded: false,
    isGalleryOpen: false,
    hasTopGalleryMounted: false,
    hasStickyMenuMounted: false,
    isLoaded: false,
    isLoadingMoreReviews: false,
    productGroup: { productData: { variants: [] } },
    reviews: [],
    reviewRatingAverage: 0,
    reviewTotal: 0,
    reviewPageLoaded: 0,
    scrollPosition: 0,
    selectedGender: GENDERS.default,
    slideIndex: INITIAL_SLIDE_INDEX,
    slideResetter: undefined,
  },

  mutations: {
    addReviews(state, payload) {
      state.reviews.push(...payload)
      state.reviewPageLoaded++
    },

    openGallery(state, payload) {
      state.isGalleryOpen = payload
    },

    setTopGalleryMounted(state, payload) {
      state.hasTopGalleryMounted = payload
    },

    setStickyMenuMounted(state, payload) {
      state.hasStickyMenuMounted = payload
    },

    setAllItemsExpanded(state, payload) {
      state.isAllItemsExpanded = payload
    },

    setCurrentCaptionIndex(state, payload) {
      state.lastCaptionIndex = state.currentCaptionIndex
      state.currentCaptionIndex = payload
    },

    setCurrentGalleryItemIndex(state, payload) {
      state.currentGalleryItemIndex = payload
    },

    setCurrentVariantIndex(state, payload) {
      state.currentVariantIndex = payload
    },

    setGender(state, payload) {
      state.selectedGender = payload
    },

    setHasFetchedFirstReviews(state, payload) {
      state.hasFetchedFirstReviews = payload
    },

    setIsLoadingMoreReviews(state, payload) {
      state.isLoadingMoreReviews = payload
    },

    setProductGroup(state, payload) {
      state.productGroup = payload
      state.isLoaded = true
    },

    setReviewRatingAverage(state, payload) {
      state.reviewRatingAverage = payload
    },

    setReviews(state, payload) {
      state.reviews = payload
      state.reviewPageLoaded = FIRST_REVIEW_PAGE
    },

    setReviewTotal(state, payload) {
      state.reviewTotal = payload
    },

    setScrollposition(state, payload) {
      state.scrollPosition = payload
    },

    setSlideIndex(state, payload) {
      state.slideIndex = payload
    },

    resetSlideIndex(state) {
      // To prevent the slide animation on reset
      // i.e. [3, false] would go to index 3 without animation
      // See vue-carousel documentation of `navigateTo` for details
      state.slideResetter = [INITIAL_SLIDE_INDEX, false]
    },
  },

  actions: {
    replaceRoute({ rootState, state, getters }, isVariantClick = true) {
      router.replace({
        name: routeNames.product,
        params: {
          ...rootState.route.params,
          gender: state.selectedGender,
          colors: getters.selectedColors,
          isVariantClick,
        },
      })
    },

    validateRoute({ rootState, getters }) {
      const routeColors = rootState.route.params.colors?.split('-') || []
      const isValidColors = findVariantIndex(getters.product.variants, routeColors) > -1
      const isValidGender = Object.values(GENDERS).includes(rootState.route.params.gender)

      if (!isValidColors || !isValidGender) {
        // Fallback to main variant which is the first male variant
        router.replace({
          name: routeNames.product,
          query: rootState.route.query,
          params: { isVariantClick: true },
        })
      }
    },

    handleGenderSwitch({ state, getters, commit, dispatch }, gender) {
      if (gender === state.selectedGender) return
      commit('setGender', gender)
      const currentVariantIndex =
        gender === GENDERS.mens ? getters.firstMaleVariantIndex : getters.firstFemaleVariantIndex
      commit('setCurrentVariantIndex', currentVariantIndex)
      commit('resetSlideIndex')
      dispatch('replaceRoute')
    },

    async fetchData({ rootState, commit, dispatch, getters }) {
      const [error, data] = await to(
        buildClient().query(productGroupQuery, {
          slug: getters.productGroupName,
        })
      )
      if (error || !data?.productGroup?.productData?.variants.length)
        return redirectToFilterPage(rootState.page.hostname, rootState.page.locale)

      const { productGroup } = data
      commit('setReviewRatingAverage', productGroup.reviewRatingAverage)
      commit('setReviewTotal', productGroup.reviewTotal)
      delete productGroup.reviewRatingAverage
      delete productGroup.reviewTotal

      commit('setProductGroup', productGroup)
      dispatch('validateRoute')
    },

    async fetchReviews({ commit, getters }) {
      const { productGroup } = await buildClient().query(productReviewsQuery, {
        slug: getters.productGroupName,
      })
      commit('setHasFetchedFirstReviews', true)

      if (productGroup) {
        Vue.nextTick(() => commit('setReviews', productGroup.reviews))
      }
    },

    async loadMoreReviews({ state, commit, getters }) {
      commit('setIsLoadingMoreReviews', true)
      const { productGroup } = await loadForAtLeast(
        buildClient().query(productReviewsQuery, {
          slug: getters.productGroupName,
          review_page: state.reviewPageLoaded + 1,
        })
      )
      commit('setIsLoadingMoreReviews', false)
      commit('addReviews', productGroup.reviews)
    },
  },

  getters: {
    currentGalleryItemIndex: state => state.currentGalleryItemIndex,
    currentVariant: (state, getters) => getters.product.variants[state.currentVariantIndex],
    currentVariantIndex: state => state.currentVariantIndex,
    currentHighlights: (state, getters) =>
      state.isLoaded ? getters.currentVariant.highlights : null,
    currentSpreeProduct: (state, getters) =>
      state.isLoaded ? getters.currentVariant.spreeProduct : null,
    currentCaptionIndex: state => state.currentCaptionIndex,
    firstMaleVariantIndex: (state, getters) =>
      getters.product.variants.findIndex(variant => variant.gender === GENDERS.mens),
    firstFemaleVariantIndex: (state, getters) =>
      getters.product.variants.findIndex(variant => variant.gender === GENDERS.womens),
    isAllItemsExpanded: state => state.isAllItemsExpanded,
    isGalleryOpen: state => state.isGalleryOpen,
    isLoaded: state => state.isLoaded,
    isVisible: (state, getters) => state.isLoaded && getters.hasLazyComponentsMounted,
    lastCaptionIndex: state => state.lastCaptionIndex,
    mainVariant: (_state, getters) => getters.product.variants[getters.firstMaleVariantIndex] || 0,
    mainVariantColors: (state, getters) =>
      state.isLoaded ? createRouteColors(getters.mainVariant.color) : '',
    product: state => state.productGroup.productData,
    productName: (_state, getters) => getters.product.name,
    productGroupName: (_state, _getters, rootState) => rootState.route.params.productGroup,
    scrollPosition: state => state.scrollPosition,
    selectedColors: (state, getters) =>
      state.isLoaded ? createRouteColors(getters.currentVariant.color) : '',
    hasLazyComponentsMounted: state => state.hasTopGalleryMounted && state.hasStickyMenuMounted,
  },
}
